import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Categories API
export const categoriesApi = {
  getAll: async (activeOnly = true, country = null) => {
    const params = { active_only: activeOnly };
    if (country) params.country = country;
    const response = await axios.get(`${API}/categories`, { params });
    return response.data;
  },
  
  getBySlug: async (slug, country = null) => {
    const params = country ? { country } : {};
    const response = await axios.get(`${API}/categories/${slug}`, { params });
    return response.data;
  }
};

// Brands API
export const brandsApi = {
  getAll: async (activeOnly = true, country = null) => {
    const params = { active_only: activeOnly };
    if (country) params.country = country;
    const response = await axios.get(`${API}/brands`, { params });
    return response.data;
  },
  
  getBySlug: async (slug, country = null) => {
    const params = country ? { country } : {};
    const response = await axios.get(`${API}/brands/${slug}`, { params });
    return response.data;
  }
};

// Products API
export const productsApi = {
  getAll: async (params = {}) => {
    const response = await axios.get(`${API}/products`, { params });
    return response.data;
  },
  
  getBySlug: async (slug, country = null) => {
    const params = country ? { country } : {};
    const response = await axios.get(`${API}/products/${slug}`, { params });
    return response.data;
  },
  
  getRelated: async (productId, limit = 8, country = null) => {
    const params = { limit };
    if (country) params.country = country;
    const response = await axios.get(`${API}/products/${productId}/related`, { params });
    return response.data;
  },
  
  getFeatured: async (limit = 8, country = null) => {
    const params = { is_featured: true, limit };
    if (country) params.country = country;
    const response = await axios.get(`${API}/products`, { params });
    return response.data;
  },
  
  getBestSelling: async (limit = 8, country = null) => {
    const params = { is_best_selling: true, limit };
    if (country) params.country = country;
    const response = await axios.get(`${API}/products`, { params });
    return response.data;
  },
  
  getOnSale: async (limit = 8, country = null) => {
    const params = { is_on_sale: true, limit };
    if (country) params.country = country;
    const response = await axios.get(`${API}/products`, { params });
    return response.data;
  }
};

// Search API
export const searchApi = {
  search: async (query, page = 1, limit = 20) => {
    const response = await axios.get(`${API}/search`, {
      params: { q: query, page, limit }
    });
    return response.data;
  },
  
  getFilterOptions: async (country = null) => {
    const params = country ? { country } : {};
    const response = await axios.get(`${API}/filters/options`, { params });
    return response.data;
  }
};

// Auth API
export const authApi = {
  register: async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    return response.data;
  },
  
  login: async (credentials) => {
    const response = await axios.post(`${API}/auth/login`, credentials);
    return response.data;
  },
  
  logout: async () => {
    const response = await axios.post(`${API}/auth/logout`);
    return response.data;
  },
  
  refreshToken: async (refreshToken) => {
    const response = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken });
    return response.data;
  },
  
  getMe: async (token) => {
    const response = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// Cart API
export const cartApi = {
  getCart: async (sessionId, country = null) => {
    const response = await axios.get(`${API}/cart`, {
      params: country ? { country } : {},
      headers: sessionId ? { 'X-Session-ID': sessionId } : {}
    });
    return response.data;
  },
  
  addToCart: async (productId, quantity = 1, variantId = null, sessionId, country = null) => {
    const params = new URLSearchParams({
      productId,
      quantity: quantity.toString()
    });
    if (variantId) params.append('variantId', variantId);
    if (country) params.append('country', country);
    
    const response = await axios.post(
      `${API}/cart/items?${params.toString()}`,
      {},
      { headers: sessionId ? { 'X-Session-ID': sessionId } : {} }
    );
    return response.data;
  },
  
  updateCartItem: async (productId, quantity, variantId = null, sessionId, country = null) => {
    const response = await axios.put(
      `${API}/cart/items/${productId}`,
      { quantity, variant_id: variantId },
      {
        params: country ? { country } : {},
        headers: sessionId ? { 'X-Session-ID': sessionId } : {}
      }
    );
    return response.data;
  },
  
  removeFromCart: async (productId, variantId = null, sessionId) => {
    const response = await axios.delete(`${API}/cart/items/${productId}`, {
      params: { variant_id: variantId },
      headers: sessionId ? { 'X-Session-ID': sessionId } : {}
    });
    return response.data;
  },
  
  clearCart: async (sessionId) => {
    const response = await axios.delete(`${API}/cart`, {
      headers: sessionId ? { 'X-Session-ID': sessionId } : {}
    });
    return response.data;
  }
};

// Orders API
export const ordersApi = {
  createOrder: async (orderData, token) => {
    const response = await axios.post(`${API}/orders`, orderData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  
  getOrders: async (page = 1, limit = 10, token) => {
    const response = await axios.get(`${API}/orders`, {
      params: { page, limit },
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  
  getOrderByNumber: async (orderNumber, token) => {
    const response = await axios.get(`${API}/orders/${orderNumber}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  
  cancelOrder: async (orderId, token) => {
    const response = await axios.put(`${API}/orders/${orderId}/cancel`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// Wishlist API
export const wishlistApi = {
  getWishlist: async (token, country = null) => {
    const params = country ? { country } : {};
    const response = await axios.get(`${API}/wishlist`, {
      params,
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  
  addToWishlist: async (productId, token) => {
    const response = await axios.post(
      `${API}/wishlist/items?product_id=${productId}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
  
  removeFromWishlist: async (productId, token) => {
    const response = await axios.delete(`${API}/wishlist/items/${productId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  
  clearWishlist: async (token) => {
    const response = await axios.post(`${API}/wishlist/clear`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// Addresses API
export const addressesApi = {
  getAddresses: async (token) => {
    const response = await axios.get(`${API}/addresses`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  
  createAddress: async (addressData, token) => {
    const response = await axios.post(`${API}/addresses`, addressData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  
  updateAddress: async (addressId, addressData, token) => {
    const response = await axios.put(`${API}/addresses/${addressId}`, addressData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  
  deleteAddress: async (addressId, token) => {
    const response = await axios.delete(`${API}/addresses/${addressId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  
  setDefaultAddress: async (addressId, token) => {
    const response = await axios.put(`${API}/addresses/${addressId}/set-default`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// Payments API
export const paymentsApi = {
  createPaymentIntent: async (orderId, token) => {
    const response = await axios.post(
      `${API}/payments/create-payment-intent`,
      { order_id: orderId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
  
  confirmPayment: async (paymentIntentId, token) => {
    const response = await axios.post(
      `${API}/payments/confirm`,
      { payment_intent_id: paymentIntentId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  }
};

export default {
  categories: categoriesApi,
  brands: brandsApi,
  products: productsApi,
  search: searchApi,
  auth: authApi,
  cart: cartApi,
  wishlist: wishlistApi,
  addresses: addressesApi,
  orders: ordersApi,
  payments: paymentsApi
};

// Admin APIs
export const adminApi = {
  // Products
  products: {
    getAll: async (token, params = {}) => {
      const response = await axios.get(`${API}/products`, {
        params: { ...params, active_only: false },
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    create: async (productData, token) => {
      const response = await axios.post(`${API}/products`, productData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    update: async (slug, productData, token) => {
      const response = await axios.put(`${API}/products/${slug}`, productData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    delete: async (slug, token) => {
      const response = await axios.delete(`${API}/products/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    }
  },
  
  // Categories
  categories: {
    getAll: async (token) => {
      const response = await axios.get(`${API}/categories/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    create: async (categoryData, token) => {
      const response = await axios.post(`${API}/categories`, categoryData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    update: async (slug, categoryData, token) => {
      const response = await axios.put(`${API}/categories/${slug}`, categoryData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    delete: async (slug, token) => {
      const response = await axios.delete(`${API}/categories/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    }
  },
  
  // Brands
  brands: {
    getAll: async (token) => {
      const response = await axios.get(`${API}/brands`, {
        params: { active_only: false },
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    create: async (brandData, token) => {
      const response = await axios.post(`${API}/brands`, brandData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    update: async (brandId, brandData, token) => {
      const response = await axios.put(`${API}/brands/${brandId}`, brandData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    delete: async (brandId, token) => {
      const response = await axios.delete(`${API}/brands/${brandId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    }
  },

  // Page Categories
  pageCategories: {
    getAll: async (token) => {
      const response = await axios.get(`${API}/page-categories`, {
        params: { active_only: false },
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    create: async (categoryData, token) => {
      const response = await axios.post(`${API}/page-categories`, categoryData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    update: async (slug, categoryData, token) => {
      const response = await axios.put(`${API}/page-categories/${slug}`, categoryData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    delete: async (slug, token) => {
      const response = await axios.delete(`${API}/page-categories/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    }
  },

  // Pages
  pages: {
    getAll: async (token, params = {}) => {
      const response = await axios.get(`${API}/pages/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    create: async (pageData, token) => {
      const response = await axios.post(`${API}/pages`, pageData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    update: async (slug, pageData, token) => {
      const response = await axios.put(`${API}/pages/${slug}`, pageData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    delete: async (pageId, token) => {
      const response = await axios.delete(`${API}/pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    }
  },
  
  // Orders
  orders: {
    getAll: async (token, params = {}) => {
      const response = await axios.get(`${API}/orders/all`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    getById: async (orderId, token) => {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    setTracking: async (orderNumber, trackingNumber, token) => {
      const response = await axios.post(
        `${API}/shipping/orders/${orderNumber}/tracking`,
        { tracking_number: trackingNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    refund: async (orderNumber, token) => {
      const response = await axios.post(
        `${API}/orders/${orderNumber}/refund`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    schedulePickup: async (orderNumber, token) => {
      const response = await axios.post(
        `${API}/shipping/orders/${orderNumber}/pickup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    createDispatch: async (orderNumber, token) => {
      const response = await axios.post(
        `${API}/shipping/orders/${orderNumber}/dispatch`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    updateStatus: async (orderId, status, trackingNumber, token) => {
      const params = { new_status: status };
      if (trackingNumber) {
        params.tracking_number = trackingNumber;
      }
      const response = await axios.put(
        `${API}/orders/${orderId}/status`,
        {},
        { 
          params,
          headers: { Authorization: `Bearer ${token}` } 
        }
      );
      return response.data;
    }
  },
  
  // Customers
  customers: {
    getAll: async (token, params = {}) => {
      const response = await axios.get(`${API}/auth/users`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    }
  },

  // Countries
  countries: {
    getAll: async (token) => {
      const response = await axios.get(`${API}/countries/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    getPricingOptions: async (token) => {
      const response = await axios.get(`${API}/countries/admin/pricing-options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    rebuildPrices: async (body, token) => {
      const response = await axios.post(`${API}/countries/admin/rebuild-prices`, body || {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });
      return response.data;
    },
    refreshRates: async (body, token) => {
      const response = await axios.post(`${API}/countries/admin/refresh-rates`, body || {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 180000
      });
      return response.data;
    },
    getFxStatus: async (token) => {
      const response = await axios.get(`${API}/countries/admin/fx-status`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 180000
      });
      return response.data;
    },
    update: async (countryId, countryData, token) => {
      const response = await axios.put(`${API}/countries/${countryId}`, countryData, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });
      return response.data;
    }
  }
};

// Pages API (Public)
export const pagesApi = {
  getAll: async (categoryId = null, country = null) => {
    const params = {};
    if (categoryId) params.category_id = categoryId;
    if (country) params.country = country;
    const response = await axios.get(`${API}/pages`, { params });
    return response.data;
  },
  
  getBySlug: async (slug, country = null) => {
    const params = country ? { country } : {};
    const response = await axios.get(`${API}/pages/${slug}`, { params });
    return response.data;
  }
};
